import { useRef, useState, useEffect, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { RouteData } from '@cya-routes/shared'

interface UseTracerMarkerProps {
  mapManager: React.MutableRefObject<any>
  currentRoute: RouteData | null
  renderersInitialized: boolean
}

interface UseTracerMarkerReturn {
  tracerPosition: { distance: number; coordinates: [number, number] } | null
  updateTracerPosition: (position: { distance: number; coordinates: [number, number] } | null) => void
}

/**
 * Custom hook that manages the elevation profile tracer marker
 * Shows a marker on the map that corresponds to the hovered position on the elevation profile
 */
export function useTracerMarker({
  mapManager,
  currentRoute,
  renderersInitialized
}: UseTracerMarkerProps): UseTracerMarkerReturn {
  const tracerMarker = useRef<mapboxgl.Marker | null>(null)
  const [tracerPosition, setTracerPosition] = useState<{ distance: number; coordinates: [number, number] } | null>(null)


  // Update tracer position callback
  const updateTracerPosition = useCallback((position: { distance: number; coordinates: [number, number] } | null) => {
    setTracerPosition(position)
  }, [])

  // Create and update tracer marker
  useEffect(() => {
    if (!mapManager.current || !renderersInitialized) return

    const map = mapManager.current.getMap()
    if (!map) return

    // Remove existing tracer if no position
    if (!tracerPosition) {
      if (tracerMarker.current) {
        tracerMarker.current.remove()
        tracerMarker.current = null
      }
      return
    }

    // Create tracer marker if it doesn't exist
    if (!tracerMarker.current) {
      const markerElement = document.createElement('div')
      markerElement.style.width = '13px'
      markerElement.style.height = '13px'
      markerElement.style.borderRadius = '50%'
      markerElement.style.background = 'radial-gradient(circle at 30% 30%, #60a5fa, #3b82f6 40%, #2563eb 100%)'
      markerElement.style.border = '1px solid #ffffff'
      markerElement.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4)'
      markerElement.style.cursor = 'pointer'
      markerElement.style.pointerEvents = 'none'
      markerElement.style.zIndex = '1000'

      tracerMarker.current = new mapboxgl.Marker({
        element: markerElement,
        anchor: 'center'
      })
    }

    // Update tracer position
    if (currentRoute && tracerPosition.coordinates) {
      tracerMarker.current
        .setLngLat(tracerPosition.coordinates)
        .addTo(map)
    }
  }, [tracerPosition, mapManager, currentRoute, renderersInitialized])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tracerMarker.current) {
        tracerMarker.current.remove()
        tracerMarker.current = null
      }
    }
  }, [])

  return {
    tracerPosition,
    updateTracerPosition
  }
}